
import { useState, useEffect, useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

interface ProcessStepProps {
  number: number;
  title: string;
  description: string;
  bulletPoints: string[];
  testimonial: string;
  isActive: boolean;
  onMouseEnter: () => void;
}

const ProcessStep = ({ 
  number, 
  title, 
  description, 
  bulletPoints, 
  testimonial, 
  isActive,
  onMouseEnter 
}: ProcessStepProps) => {
  return (
    <div 
      className={`relative flex flex-col sm:flex-row transition-all duration-300 ${isActive ? 'scale-[1.02]' : 'opacity-90 hover:opacity-100'}`}
      onMouseEnter={onMouseEnter}
    >
      <div className="sm:w-12 flex-shrink-0">
        <div className={`w-12 h-12 ${isActive ? 'bg-edgefi-purple' : 'bg-edgefi-amethyst'} rounded-full flex items-center justify-center text-white font-bold shadow-lg z-10 relative transition-colors duration-300`}>
          {number}
        </div>
      </div>
      <div className={`mt-4 sm:mt-0 sm:ml-8 bg-white p-6 rounded-xl shadow-sm border ${isActive ? 'border-edgefi-purple/30' : 'border-gray-100'} transition-all duration-300`}>
        <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
        <p className="text-gray-600 mb-4">
          {description}
        </p>
        <ul className="space-y-2">
          {bulletPoints.map((point, index) => (
            <li key={index} className="flex items-start gap-2">
              <div className={`min-w-4 h-4 w-4 rounded-full ${isActive ? 'bg-edgefi-purple/20' : 'bg-edgefi-amethyst/10'} flex-shrink-0 mt-1 transition-colors duration-300`}></div>
              <span className="text-gray-700">{point}</span>
            </li>
          ))}
        </ul>
        {isActive && (
          <div className="mt-4 p-3 bg-edgefi-purple/5 rounded-lg border border-edgefi-purple/10 animate-fade-in">
            <p className="text-gray-600 italic text-sm">
              "{testimonial}"
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export const ProcessSection = () => {
  const [activeStep, setActiveStep] = useState(1);
  const sectionRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<(HTMLDivElement | null)[]>([]);
  const isMobile = useIsMobile();
  
  const processSteps = [
    {
      number: 1,
      title: "We Listen to Your Unique Challenges",
      description: "Your journey begins with us understanding what matters most to your business:",
      bulletPoints: [
        "Share your current frustrations and technology pain points without any technical jargon",
        "Help us understand your business goals and how technology should be supporting them",
        "Discuss the impact of IT issues on your team's productivity and customer satisfaction"
      ],
      testimonial: "For the first time, an IT company actually listened to our business goals before talking about servers and networks. They found issues we didn't even know were slowing us down."
    },
    {
      number: 2,
      title: "Your Personalized Technology Strategy",
      description: "Receive a plan tailored to your specific business needs and goals:",
      bulletPoints: [
        "Get clear recommendations prioritized by what will have the greatest positive impact",
        "See exactly how technology will better support your business objectives",
        "Understand the costs with transparent, predictable pricing that eliminates surprises"
      ],
      testimonial: "The roadmap gave us clarity about our technology for the next 3 years. We could finally budget for IT with confidence and stop worrying about surprise expenses."
    },
    {
      number: 3,
      title: "Seamless Implementation with No Disruption",
      description: "Experience a smooth transition that doesn't interfere with your operations:",
      bulletPoints: [
        "Continue serving your customers without interruption during the implementation",
        "Gain immediate visibility into your systems with monitoring tools giving you peace of mind",
        "Receive clear communication at every step so you always know what's happening"
      ],
      testimonial: "The transition was so smooth our team barely noticed it was happening. We expected at least some downtime, but there was virtually no disruption to our daily operations."
    },
    {
      number: 4,
      title: "Your Technology Working for You, Not Against You",
      description: "Enjoy a new relationship with technology that empowers your business growth:",
      bulletPoints: [
        "Experience issues being resolved before they affect your team or customers",
        "Receive regular reviews showing how technology is supporting your business goals",
        "Focus on your core business while your IT consistently supports your success"
      ],
      testimonial: "The quarterly reviews are invaluable. They show us exactly how our technology is performing and what we should be planning for next. For the first time, IT is actually helping us grow rather than holding us back."
    }
  ];

  // Handle scroll-based activation only on mobile
  useEffect(() => {
    // If not mobile, don't add scroll listeners
    if (!isMobile) return;
    
    const handleScroll = () => {
      if (!sectionRef.current) return;
      
      // Only run intersection checks if section is in viewport
      const sectionRect = sectionRef.current.getBoundingClientRect();
      const isSectionInView = sectionRect.top < window.innerHeight && sectionRect.bottom > 0;
      
      if (isSectionInView) {
        // Find which step is most centered in the viewport
        stepsRef.current.forEach((step, index) => {
          if (!step) return;
          
          const rect = step.getBoundingClientRect();
          const isInView = rect.top < window.innerHeight * 0.75 && rect.bottom > window.innerHeight * 0.25;
          
          if (isInView) {
            setActiveStep(index + 1);
          }
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Initial check on mount
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  // Set first item as active by default when switching to desktop 
  useEffect(() => {
    if (!isMobile) {
      setActiveStep(1);
    }
  }, [isMobile]);

  return (
    <section className="py-16 bg-white" id="process" ref={sectionRef}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-edgefi-purple/10 text-edgefi-purple rounded-full text-sm font-medium mb-5">Your Journey</span>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
            Your Path From IT Frustration to Technology Freedom
          </h2>
          <p className="text-xl text-gray-600">
            A simple journey to transform your technology experience so you can focus on what you do best—running your business.
          </p>
        </div>
        
        <div className="max-w-5xl mx-auto">
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gradient-to-b from-edgefi-purple/30 via-edgefi-amethyst/20 to-edgefi-purple/30 hidden sm:block"></div>
            
            <div className="space-y-12">
              {processSteps.map((step, index) => (
                <div 
                  key={step.number} 
                  ref={el => stepsRef.current[index] = el}
                >
                  <ProcessStep 
                    number={step.number}
                    title={step.title}
                    description={step.description}
                    bulletPoints={step.bulletPoints}
                    testimonial={step.testimonial}
                    isActive={activeStep === step.number}
                    onMouseEnter={() => setActiveStep(step.number)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
